import React, { FC, useMemo } from 'react';
import { useForm, Controller, ControllerRenderProps } from 'react-hook-form';
import {
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  FormLabel,
  FormGroup,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  AutocompleteRenderInputParams,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ReportConfigDto,
  ReportOccurrence,
  ReportType,
  DeviceGroupDto,
} from '@thingslog/repositories';
import { deviceGroupsQueryClient } from '../../../clients/ReactQueryClients/ReactQueryClients';
import useDetermineCompanyId from '../../../hooks/useDetermineCompanyId';
import ReportSettingsFormFactory from './ReportSettingsFormFactory';
import { LocalizedDatePicker } from '@thingslog/ui-components';
import { startOfDay, endOfDay } from 'date-fns';

interface ReportConfigFormProps {
  defaultValues?: Partial<ReportConfigDto>;
  onSubmit: (data: ReportConfigDto) => void;
  onCancel: () => void;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const ReportConfigForm: FC<ReportConfigFormProps> = ({
  defaultValues,
  onSubmit,
  onCancel,
  isEdit = false,
  isSubmitting = false,
}: ReportConfigFormProps): React.ReactElement => {
  const { t } = useTranslation();
  const selectedCompanyId = useDetermineCompanyId();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ReportConfigDto>({
    defaultValues: {
      reportName: '',
      deviceGroup: [],
      reportOccurrence: ReportOccurrence.DAILY,
      reportSettings: {
        reportType: ReportType.CONSUMPTION_REPORT,
      },
      emailList: '',
      companyId: 0,
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
      lastExecution: new Date().toISOString(),
      nextExecution: new Date().toISOString(),
      calcMin: false,
      calcMax: false,
      calcAverage: false,
      ...defaultValues,
    },
  });

  const { useDeviceGroupsData } = useMemo(() => deviceGroupsQueryClient, []);
  const { data: deviceGroups, isLoading: isLoadingGroups } = useDeviceGroupsData(
    selectedCompanyId,
    {
      enabled: selectedCompanyId !== null,
    }
  );

  const deviceGroupOptions: string[] = useMemo(() => {
    if (!deviceGroups) return [];
    return deviceGroups.map((group: DeviceGroupDto) => group.deviceGroupName);
  }, [deviceGroups]);

  // Watch the report type to render the appropriate settings form
  const selectedReportType = watch('reportSettings.reportType');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <TextField
        {...register('reportName', { required: true })}
        label={t('report_config_form_report_name')}
        size="small"
        fullWidth
        required
        disabled={isEdit}
        error={!!errors.reportName}
        helperText={errors.reportName ? t('report_config_form_field_required') : ''}
      />

      <Controller
        name="reportSettings.reportType"
        control={control}
        rules={{ required: true }}
        render={({
          field,
        }: {
          field: ControllerRenderProps<ReportConfigDto, 'reportSettings.reportType'>;
        }): React.ReactElement => (
          <FormControl size="small" fullWidth required>
            <InputLabel>{t('report_config_form_report_type')}</InputLabel>
            <Select {...field} label={t('report_config_form_report_type')}>
              <MenuItem value={ReportType.CONSUMPTION_REPORT}>
                {t('report_type_consumption')}
              </MenuItem>
              <MenuItem value={ReportType.CONSUMPTION_EXPORT_REPORT}>
                {t('report_type_consumption_export')}
              </MenuItem>
              <MenuItem value={ReportType.CUSTOMER_BILLING_REPORT}>
                {t('report_type_customer_billing')}
              </MenuItem>
              <MenuItem value={ReportType.ALARM_REPORT}>{t('report_type_alarm')}</MenuItem>
              <MenuItem value={ReportType.TRANSMISSION_REPORT}>
                {t('report_type_transmission')}
              </MenuItem>
              <MenuItem value={ReportType.LICENSE_REPORT}>{t('report_type_license')}</MenuItem>
            </Select>
          </FormControl>
        )}
      />

      <Controller
        name="reportOccurrence"
        control={control}
        rules={{ required: true }}
        render={({
          field,
        }: {
          field: ControllerRenderProps<ReportConfigDto, 'reportOccurrence'>;
        }): React.ReactElement => (
          <FormControl size="small" fullWidth required>
            <InputLabel>{t('report_config_form_occurrence')}</InputLabel>
            <Select {...field} label={t('report_config_form_occurrence')}>
              <MenuItem value={ReportOccurrence.DAILY}>{t('report_occurrence_daily')}</MenuItem>
              <MenuItem value={ReportOccurrence.WEEKLY}>{t('report_occurrence_weekly')}</MenuItem>
              <MenuItem value={ReportOccurrence.MONTHLY}>{t('report_occurrence_monthly')}</MenuItem>
            </Select>
          </FormControl>
        )}
      />

      <Controller
        name="deviceGroup"
        control={control}
        rules={{
          validate: (value: string[]): string | true => {
            if (!value || value.length === 0) {
              return String(
                t('report_config_form_device_groups_required') ||
                  'At least one device group must be selected'
              );
            }
            return true;
          },
        }}
        render={({
          field: { onChange, value },
        }: {
          field: { onChange: (value: string[]) => void; value: string[] };
        }): React.ReactElement => (
          <Autocomplete<string, true>
            multiple
            options={deviceGroupOptions}
            value={value || []}
            onChange={(event: React.SyntheticEvent, newValue: string[]): void => {
              onChange(newValue);
            }}
            disabled={isLoadingGroups}
            renderInput={(params: AutocompleteRenderInputParams): React.ReactElement => (
              <TextField
                {...params}
                label={t('report_config_form_device_groups')}
                size="small"
                error={!!errors.deviceGroup}
                helperText={errors.deviceGroup?.message as string}
              />
            )}
          />
        )}
      />

      <TextField
        {...register('emailList', {
          required: true,
          validate: (value: string): string | true => {
            if (!value || value.trim() === '') {
              return String(t('report_config_form_field_required'));
            }

            // Check for spaces in email addresses (before trimming)
            if (value.includes(' ')) {
              return String(t('report_config_form_email_no_spaces'));
            }

            // Split by comma and validate each email
            const emails = value
              .split(',')
              .map((email: string) => email.trim())
              .filter((email: string) => email !== '');

            if (emails.length === 0) {
              return String(t('report_config_form_email_list_required'));
            }

            // Email validation regex
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            for (const email of emails) {
              if (!emailRegex.test(email)) {
                return String(t('report_config_form_email_invalid', { email }));
              }
            }

            return true;
          },
        })}
        label={t('report_config_form_email_list')}
        size="small"
        fullWidth
        required
        multiline
        rows={2}
        error={!!errors.emailList}
        helperText={
          errors.emailList ? errors.emailList.message : t('report_config_form_email_list_hint')
        }
      />

      <div className="flex gap-4">
        <Controller
          name="startDate"
          control={control}
          rules={{ required: true }}
          render={({
            field,
          }: {
            field: ControllerRenderProps<ReportConfigDto, 'startDate'>;
          }): React.ReactElement => (
            <LocalizedDatePicker
              label={t('report_config_form_start_date')}
              value={field.value ? new Date(field.value) : null}
              onChange={(date: Date | null): void => {
                field.onChange(date ? startOfDay(date).toISOString() : '');
              }}
              inputFormat="dd/MM/yyyy"
              renderInputTextField={{
                size: 'small',
                fullWidth: true,
                required: true,
                error: !!errors.startDate,
                helperText: errors.startDate ? t('report_config_form_field_required') : '',
              }}
            />
          )}
        />

        <Controller
          name="endDate"
          control={control}
          render={({
            field,
          }: {
            field: ControllerRenderProps<ReportConfigDto, 'endDate'>;
          }): React.ReactElement => (
            <LocalizedDatePicker
              label={t('report_config_form_end_date')}
              value={field.value ? new Date(field.value) : null}
              onChange={(date: Date | null): void => {
                field.onChange(date ? endOfDay(date).toISOString() : '');
              }}
              inputFormat="dd/MM/yyyy"
              renderInputTextField={{
                size: 'small',
                fullWidth: true,
                error: !!errors.endDate,
                helperText: errors.endDate ? t('report_config_form_field_required') : '',
              }}
            />
          )}
        />
      </div>

      <Box className="border border-gray-300 rounded p-4">
        <FormLabel component="legend" className="mb-2">
          {t('report_config_form_calculate')}
        </FormLabel>
        <FormGroup row>
          <FormControlLabel
            control={<Checkbox {...register('calcMin')} checked={watch('calcMin')} />}
            label={t('report_config_form_calc_min')}
          />
          <FormControlLabel
            control={<Checkbox {...register('calcMax')} checked={watch('calcMax')} />}
            label={t('report_config_form_calc_max')}
          />
          <FormControlLabel
            control={<Checkbox {...register('calcAverage')} checked={watch('calcAverage')} />}
            label={t('report_config_form_calc_average')}
          />
        </FormGroup>
      </Box>

      {/* Render the appropriate report settings sub-form based on selected report type */}
      {selectedReportType && (
        <ReportSettingsFormFactory
          reportType={selectedReportType}
          control={control}
          watch={watch}
        />
      )}

      <div className="flex gap-4 justify-end mt-4">
        <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          {t('report_config_form_cancel')}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
          {isEdit ? t('report_config_form_update') : t('report_config_form_create')}
        </Button>
      </div>
    </form>
  );
};

export default ReportConfigForm;
